/**
 * Utility functions for sorting locations consistently across the app
 */

/**
 * Custom sort order for Washington state locations
 * This matches the ordering used in the primary navigation dropdown
 */
const WASHINGTON_LOCATION_ORDER = [
  'bothell',
  'redmond',
  'maple-valley',
  'gig-harbor',
  'tacoma',
  'puyallup',
  'richland',
  'kennewick'
];

/**
 * Sort locations by the default ordering used in the navigation dropdown
 * - Washington locations: Custom order as defined in WASHINGTON_LOCATION_ORDER
 * - Other regions: Alphabetically by name
 * 
 * @param {Array} locations - Array of location objects
 * @returns {Array} Sorted array of locations
 */
export function sortLocationsByDefault(locations) {
  if (!locations || locations.length === 0) return locations;
  
  return [...locations].sort((a, b) => {
    const regionA = a.region || '';
    const regionB = b.region || '';
    
    // If both are Washington locations, use custom order
    if (regionA === 'washington' && regionB === 'washington') {
      // Extract city from slug (e.g., "bothell-wa" -> "bothell")
      const cityA = a.slug?.split('-').slice(0, -1).join('-') || a.slug;
      const cityB = b.slug?.split('-').slice(0, -1).join('-') || b.slug;
      
      const indexA = WASHINGTON_LOCATION_ORDER.indexOf(cityA);
      const indexB = WASHINGTON_LOCATION_ORDER.indexOf(cityB);
      
      // If both are in the order list, sort by order
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      // If only one is in the list, prioritize it
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      // Otherwise, sort alphabetically by name
      return a.name.localeCompare(b.name);
    }
    
    // Washington locations come before other regions
    if (regionA === 'washington' && regionB !== 'washington') return -1;
    if (regionA !== 'washington' && regionB === 'washington') return 1;
    
    // Indiana comes before New York
    if (regionA === 'indiana' && regionB === 'new-york') return -1;
    if (regionA === 'new-york' && regionB === 'indiana') return 1;
    
    // For same region (non-Washington) or unknown regions, sort alphabetically by name
    return a.name.localeCompare(b.name);
  });
}

/**
 * Calculate distance between two coordinates using Haversine formula
 * 
 * @param {number} lat1 - Latitude of first point
 * @param {number} lon1 - Longitude of first point
 * @param {number} lat2 - Latitude of second point
 * @param {number} lon2 - Longitude of second point
 * @returns {number} Distance in miles, rounded to 1 decimal place
 */
export function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 3958.8; // Radius of Earth in miles
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  
  // Round to 1 decimal place
  return Math.round(distance * 10) / 10;
}

function deg2rad(deg) {
  return deg * (Math.PI / 180);
}

